import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import {
  IFilter,
  IGrupo,
  IPopulate,
  IQueryParam,
  IRecorrido,
} from 'modelos/src';
import { Subscription } from 'rxjs';
import { Router, ActivatedRoute } from '@angular/router';
import { RecorridosService } from '../../../../../auxiliares/servicios/http/recorridos.service';
import { FormularioDialogService } from '../../../../../auxiliares/servicios/formulario-dialog.service';
import {
  IColumnas,
  ICrearBoton,
} from '../../../../../auxiliares/tabla/tabla.component';
import {
  IFiltroTabla,
  IRegExpSearch,
} from '../../../../../auxiliares/tabla/filtro/filtro.component';
import { HelperService } from '../../../../../auxiliares/servicios/helper.service';
import { DialogService } from '../../../../../auxiliares/dialog/dialog.service';
import {
  LineStringMapa,
  MapaFacilData,
} from '../../../../../auxiliares/mapa-facil/interfaces';
import { MapaFacilDialogComponent } from '../../../../../auxiliares/mapa-facil-dialog/mapa-facil-dialog.component';
import { ParamsService } from '../../../../../auxiliares/servicios/params.service';
import { Lista2Service } from '../../../../../auxiliares/servicios/lista2.service';
import { ClienteJerarquiaService } from '../../../../../auxiliares/servicios/cliente-jerarquia.service';

@Component({
  selector: 'app-listado-recorridos',
  templateUrl: './listado-recorridos.component.html',
  styleUrl: './listado-recorridos.component.scss',
  standalone: false,
})
export class ListadoRecorridosComponent implements OnInit, OnDestroy {
  private clienteJerarquia = inject(ClienteJerarquiaService);
  private lista2 = inject(Lista2Service);
  helper = inject(HelperService);
  private dialogService = inject(DialogService);
  private service = inject(RecorridosService);
  private navigator = inject(Router);
  private paramsService = inject(ParamsService);
  private route = inject(ActivatedRoute);
  private formularios = inject(FormularioDialogService);

  //// Tabla nueva
  public name = 'ListadoRecorridosComponent';
  public recorridos?: IRecorrido[];
  public totalCount = 0;
  public columnas?: IColumnas<IRecorrido>[];

  //Subs
  public recorridos$?: Subscription;
  //
  public botonCrear: ICrearBoton = {
    mostrar: this.helper.tienePermiso('Colectivos - Crear recorridos'),
    tooltip: 'Crear recorrido',
    color: 'primary',
    tipo: 'material',
    icon: 'add',
    accion: () => this.crear(),
  };
  public search: IRegExpSearch = {
    fields: ['nombre'],
  };
  // FILTROS
  public cliente: IFiltroTabla = {
    filter: {
      field: 'idCliente',
    },
    label: 'Cliente',
    tipo: 'cliente-selector',
  };
  // Cliente en cuya jerarquía cuelga la entidad: el cliente elegido y toda su
  // descendencia.
  public ancestro: IFiltroTabla = {
    filter: {
      field: 'idsAncestros',
      incluirPropio: true,
    },
    label: 'En la jerarquía de',
    tipo: 'cliente-selector',
  };
  public grupo: IFiltroTabla = {
    elementos: [],
    filter: {
      field: 'idGrupo',
    },
    label: 'Línea',
    selectLabel: 'nombre',
    selectValue: '_id',
    tipo: 'ngselect-async',
    asyncFunction: 'grupos',
    searchOn: ['nombre'],
  };
  public filtros: IFiltroTabla[] = [this.grupo];
  //Query
  public filter: IFilter<IRecorrido> = { categoria: 'Colectivo' };
  public populate: IPopulate[] = [
    {
      path: 'grupo',
    },
    {
      path: 'cliente',
      select: 'nombre',
    },
  ];
  public query: IQueryParam = {
    filter: JSON.stringify(this.filter),
    populate: JSON.stringify(this.populate),
    sort: 'nombre',
    page: 0,
    limit: +this.helper.getPageSize(this.name),
    includeChildren: true,
    childrenLevel: 0,
  };
  // Listars
  public async listar(): Promise<void> {
    this.recorridos$?.unsubscribe();
    this.recorridos$ = this.lista2
      .recorridos(this.query, 30)
      .subscribe((data) => {
        this.totalCount = data.totalCount;
        this.recorridos = data.datos;
      });
  }

  // Acciones
  public async crear() {
    this.paramsService.setParams({
      recorrido: null,
    });
    this.navigator.navigate(['crear'], { relativeTo: this.route });
  }

  public async editar(recorrido: IRecorrido) {
    this.paramsService.setParams({
      recorrido: recorrido,
    });
    this.navigator.navigate(['editar'], {
      relativeTo: this.route,
    });
  }

  public async ubicar(r: IRecorrido) {
    // Usar las coordenadas del geojson si existen
    const coords = r.geojson?.coordinates;
    if (!coords || coords.length === 0) {
      this.helper.notifError('El recorrido no tiene coordenadas');
      return;
    }

    const polyline: LineStringMapa = {
      color: r.color,
      points: coords,
      width: 6,
      centrar: true,
    };
    const data: MapaFacilData = {
      polylines: [polyline],
      zoom: 14,
    };
    this.formularios.abrir(MapaFacilDialogComponent, {
      tamano: 'grande',
      data,
      cerrarAfuera: true,
    });
  }

  public async eliminar(recorrido: IRecorrido) {
    const res = await this.dialogService.confirm(
      'Eliminar recorrido',
      `¿Querés eliminar el recorrido ${recorrido.nombre}?`,
    );
    if (!res) return;
    try {
      await this.service.delete(recorrido._id!);
      this.helper.notifSuccess('Recorrido eliminado');
      await this.listar();
    } catch (error) {
      console.error(error);
      this.helper.notifError(error);
    }
  }

  // Tabla

  private setColumnas() {
    this.columnas = [
      // Grupo
      {
        header: {
          label: 'Línea',
        },
        row: {
          parse(dato) {
            return dato.grupo?.nombre;
          },
          noData: 'Sin línea',
        },
      },
      // Nombre
      {
        header: {
          label: 'Nombre',
        },
        row: {
          field: 'nombre',
          noData: 'Sin nombre',
        },
      },
      // Destino
      {
        header: {
          label: 'Destino',
        },
        row: {
          field: 'destino',
          noData: 'Sin destino',
        },
      },
      // Por
      {
        header: {
          label: 'Por',
        },
        row: {
          field: 'por',
          noData: 'Sin descripción',
        },
      },
      // Paradas
      {
        header: {
          label: 'Paradas',
        },
        row: {
          parse(dato) {
            return dato.paradas?.length;
          },
          noData: 'Sin paradas',
        },
      },
    ];
    // Cliente
    if (HelperService.getTipoCliente() !== 'Final') {
      {
        this.columnas.push({
          header: {
            label: 'Cliente',
          },
          row: {
            html: (dato) => this.clienteJerarquia.htmlNombre(dato.cliente),
            tooltip: (dato) =>
              this.clienteJerarquia.tooltipJerarquia(dato.cliente),
            noData: 'Sin cliente',
          },
        });
      }
    }
    // Acciones
    this.columnas.push({
      header: {
        label: 'Acciones',
      },
      row: {
        acciones: [
          {
            tipo: 'material',
            color: 'primary',
            icon: 'map',
            tooltip: 'Ver en el mapa',
            click: (dato) => this.ubicar(dato),
            disabled: () =>
              !this.helper.tienePermiso('Colectivos - Editar recorridos'),
          },
          {
            tipo: 'material',
            color: 'primary',
            icon: 'edit',
            tooltip: 'Editar',
            click: (dato) => this.editar(dato),
            disabled: () =>
              !this.helper.tienePermiso('Colectivos - Editar recorridos'),
          },
          {
            tipo: 'material',
            color: 'warn',
            icon: 'delete',
            tooltip: 'Eliminar',
            click: (dato) => this.eliminar(dato),
            disabled: () =>
              !this.helper.tienePermiso('Colectivos - Eliminar recorridos'),
          },
        ],
      },
    });
  }

  // Hooks

  async ngOnInit() {
    if (HelperService.getTipoCliente() !== 'Final') {
      this.filtros.push(this.cliente);
      this.filtros.push(this.ancestro);
    }
    this.setColumnas();
    await Promise.all([this.listar()]);
  }

  ngOnDestroy() {
    this.recorridos$?.unsubscribe();
  }
}
